import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { X, Minimize2, Maximize2, Youtube, ExternalLink, Move, Pin, PinOff } from 'lucide-react';

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          playerVars?: {
            autoplay?: 0 | 1;
            controls?: 0 | 1;
            modestbranding?: 0 | 1;
            rel?: 0 | 1;
            start?: number;
          };
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
            onError?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        UNSTARTED: -1;
        ENDED: 0;
        PLAYING: 1;
        PAUSED: 2;
        BUFFERING: 3;
        CUED: 5;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  setPlaybackRate: (rate: number) => void;
  getPlaybackRate: () => number;
  destroy: () => void;
}

interface YouTubePlayerProps {
  youtubeUrl: string;
  isVisible: boolean;
  onClose: () => void;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayStateChange: (isPlaying: boolean) => void;
  onReady: () => void;
  currentTime?: number;
  mediaSeekRequestId?: number;
  mediaSeekRequestTime?: number | null;
  isPlaying?: boolean;
  volume?: number;
  playbackRate?: number;
  isDarkMode: boolean;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  onFloatingChange?: (isFloating: boolean) => void;
  isMobile?: boolean;
  mobileTopicTitle?: string | null;
}

// Extract video ID from various YouTube URL formats
const extractVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Load YouTube IFrame API script
let apiLoadPromise: Promise<void> | null = null;
const loadYouTubeAPI = (): Promise<void> => {
  if (apiLoadPromise) return apiLoadPromise;

  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }

  apiLoadPromise = new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;

    window.onYouTubeIframeAPIReady = () => {
      resolve();
    };

    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
  });

  return apiLoadPromise;
};

// Generate a stable unique ID per component instance
let playerIdCounter = 0;

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  youtubeUrl,
  isVisible,
  onClose,
  onTimeUpdate,
  onDurationChange,
  onPlayStateChange,
  onReady,
  currentTime,
  mediaSeekRequestId = 0,
  mediaSeekRequestTime = null,
  isPlaying,
  volume = 1,
  playbackRate = 1,
  isDarkMode,
  isMinimized = false,
  onToggleMinimize,
  onFloatingChange,
  isMobile = false,
  mobileTopicTitle = null,
}) => {
  // Generate stable player ID once per component mount
  const playerIdRef = useRef<string | null>(null);
  if (playerIdRef.current === null) {
    playerIdCounter += 1;
    playerIdRef.current = `youtube-player-${playerIdCounter}`;
  }
  const playerId = playerIdRef.current;

  const playerRef = useRef<YTPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeUpdateIntervalRef = useRef<number | null>(null);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastSeekTimeRef = useRef<number>(0);
  const lastHandledSeekRequestIdRef = useRef(0);
  const isSeekingRef = useRef(false);

  // Store callbacks in refs to avoid effect re-runs
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onDurationChangeRef = useRef(onDurationChange);
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const onReadyRef = useRef(onReady);

  // Update refs when callbacks change
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);
  useEffect(() => { onDurationChangeRef.current = onDurationChange; }, [onDurationChange]);
  useEffect(() => { onPlayStateChangeRef.current = onPlayStateChange; }, [onPlayStateChange]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  // Floating state
  const [isFloating, setIsFloating] = useState(false);
  const [floatPosition, setFloatPosition] = useState({ x: 20, y: 100 });
  const [floatSize, setFloatSize] = useState({ width: 400, height: 225 }); // 16:9 ratio
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ x: 0, y: 0 });
  const sizeStartRef = useRef({ width: 0, height: 0 });

  useEffect(() => {
    if (isMobile) {
      setIsFloating(false);
    }
  }, [isMobile]);

  // Notify parent when floating state changes
  useEffect(() => {
    onFloatingChange?.(isFloating);
  }, [isFloating, onFloatingChange]);

  const videoId = useMemo(() => extractVideoId(youtubeUrl), [youtubeUrl]);

  // Load YouTube API
  useEffect(() => {
    loadYouTubeAPI().then(() => {
      setIsAPIReady(true);
    }).catch((err) => {
      console.error('[YouTubePlayer] Failed to load YouTube API:', err);
      setError('Failed to load YouTube player');
    });
  }, []);

  // Initialize player when API is ready and visible
  useEffect(() => {
    if (!isAPIReady || !videoId || !isVisible || isMinimized) {
      return;
    }

    // Check if player already exists and is for the same video
    if (playerRef.current) {
      return;
    }

    // Wait for DOM element to be ready
    const initTimer = setTimeout(() => {
      const playerElement = document.getElementById(playerId);
      if (!playerElement) {
        console.error('[YouTubePlayer] Player element not found:', playerId);
        return;
      }

      try {
        playerRef.current = new window.YT.Player(playerId, {
          videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            modestbranding: 1,
            rel: 0,
          },
          events: {
            onReady: (event) => {
              setIsPlayerReady(true);
              const duration = event.target.getDuration();
              if (duration > 0) {
                onDurationChangeRef.current(duration);
              }
              onReadyRef.current();
            },
            onStateChange: (event) => {
              const state = event.data;
              if (state === window.YT.PlayerState.PLAYING) {
                onPlayStateChangeRef.current(true);
              } else if (state === window.YT.PlayerState.PAUSED || state === window.YT.PlayerState.ENDED) {
                onPlayStateChangeRef.current(false);
              }
            },
            onError: (event) => {
              console.error('[YouTubePlayer] Error:', event.data);
              setError('Video unavailable or restricted');
            },
          },
        });
      } catch (err) {
        console.error('[YouTubePlayer] Failed to create player:', err);
        setError('Failed to initialize YouTube player');
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
    };
  }, [isAPIReady, videoId, isVisible, isMinimized, playerId]);

  // Cleanup player when videoId changes or component unmounts
  useEffect(() => {
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // Already destroyed
        }
        playerRef.current = null;
        setIsPlayerReady(false);
      }
    };
  }, [videoId]);

  // Set up time update interval
  // MOBILE OPTIMIZATION: Use longer polling interval on mobile (500ms) vs desktop (250ms)
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current || !isVisible) return;

    // Mobile devices poll less frequently to save CPU
    const pollIntervalMs = isMobile ? 500 : 250;

    timeUpdateIntervalRef.current = window.setInterval(() => {
      if (playerRef.current && !isSeekingRef.current) {
        try {
          const time = playerRef.current.getCurrentTime();
          // Only report time if we're not in the middle of a seek
          // and enough time has passed since last seek
          const now = Date.now();
          if (now - lastSeekTimeRef.current > 150) {
            onTimeUpdateRef.current(time);
          }
        } catch (e) {
          // Player might be destroyed
        }
      }
    }, pollIntervalMs);

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, [isPlayerReady, isMobile, isVisible]);

  // Sync play state from parent
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    try {
      const playerState = playerRef.current.getPlayerState();
      const isCurrentlyPlaying = playerState === window.YT.PlayerState.PLAYING;

      if (isPlaying && !isCurrentlyPlaying) {
        playerRef.current.playVideo();
      } else if (!isPlaying && isCurrentlyPlaying) {
        playerRef.current.pauseVideo();
      }
    } catch (e) {
      // Player might be destroyed
    }
  }, [isPlaying, isPlayerReady]);

  // Sync seek from parent (when clicking on transcript timestamps or arrow keys)
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current || currentTime === undefined) return;

    // Keep parent-driven seeks responsive while still avoiding redundant loops.
    const now = Date.now();
    if (now - lastSeekTimeRef.current < 75) return;

    try {
      const playerTime = playerRef.current.getCurrentTime();
      const diff = Math.abs(playerTime - currentTime);

      // Seek if difference is more than 1 second (for arrow keys which seek 15s at a time)
      if (diff > 1) {
        isSeekingRef.current = true;
        lastSeekTimeRef.current = now;
        playerRef.current.seekTo(currentTime, true);

        // Reset seeking flag after YouTube has time to update
        setTimeout(() => {
          isSeekingRef.current = false;
        }, 150);
      }
    } catch (e) {
      // Player might be destroyed
    }
  }, [currentTime, isPlayerReady]);

  useEffect(() => {
    if (!isPlayerReady || !playerRef.current || mediaSeekRequestTime === null || mediaSeekRequestId === 0) {
      return;
    }

    if (lastHandledSeekRequestIdRef.current === mediaSeekRequestId) {
      return;
    }

    lastHandledSeekRequestIdRef.current = mediaSeekRequestId;
    const now = Date.now();

    try {
      isSeekingRef.current = true;
      lastSeekTimeRef.current = now;
      playerRef.current.seekTo(mediaSeekRequestTime, true);
      setTimeout(() => {
        isSeekingRef.current = false;
      }, 150);
    } catch (e) {
      // Player might be destroyed
    }
  }, [currentTime, isPlayerReady, mediaSeekRequestId, mediaSeekRequestTime]);

  // Sync volume
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    try {
      playerRef.current.setVolume(volume * 100);
    } catch (e) {
      // Player might be destroyed
    }
  }, [volume, isPlayerReady]);

  // Sync playback rate
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    try {
      playerRef.current.setPlaybackRate(playbackRate);
    } catch (e) {
      // Player might be destroyed
    }
  }, [playbackRate, isPlayerReady]);

  // Drag handlers for floating mode
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isFloating) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { ...floatPosition };
  }, [isFloating, floatPosition]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setFloatPosition({
        x: Math.max(0, Math.min(window.innerWidth - 320, positionStartRef.current.x + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 200, positionStartRef.current.y + dy)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Resize handler for floating mode
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isFloating) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    sizeStartRef.current = { ...floatSize };
  }, [isFloating, floatSize]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      // Maintain 16:9 aspect ratio
      const newWidth = Math.max(280, Math.min(800, sizeStartRef.current.width + dx));
      const newHeight = newWidth * (9 / 16);
      setFloatSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isVisible || !videoId) return null;

  // Mobile layout - simplified, full-width, fixed aspect ratio
  if (isMobile) {
    return (
      <div className={`w-full ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        {/* Simplified header */}
        <div className={`flex items-center justify-between px-3 py-2 border-b ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex min-w-0 items-center gap-2">
            <Youtube size={16} className="text-red-600" />
            <span className={`shrink-0 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Video
            </span>
            {mobileTopicTitle && (
              <span className={`truncate text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {mobileTopicTitle}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${
              isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            <X size={18} />
          </button>
        </div>
        {/* Video container with 16:9 aspect ratio */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          {error ? (
            <div className={`absolute inset-0 flex items-center justify-center ${
              isDarkMode ? 'text-red-400' : 'text-red-600'
            }`}>
              <span className="text-sm">{error}</span>
            </div>
          ) : (
            <div
              id={playerId}
              className="absolute inset-0 w-full h-full"
            />
          )}
        </div>
      </div>
    );
  }

  // Floating mode styles
  const floatingStyles: React.CSSProperties = isFloating ? {
    position: 'fixed',
    left: floatPosition.x,
    top: floatPosition.y,
    width: isMinimized ? '200px' : `${floatSize.width}px`,
    zIndex: 1000,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    borderRadius: '8px',
    overflow: 'hidden',
  } : {};

  // Docked mode styles - fills container width when docked
  const dockedStyles: React.CSSProperties = !isFloating ? {
    width: '100%',
    maxWidth: isMinimized ? '200px' : undefined, // Remove maxWidth when not minimized to fill container
    height: isMinimized ? 'auto' : 'fit-content',
  } : {};

  return (
    <div
      ref={containerRef}
      style={{ ...floatingStyles, ...dockedStyles }}
      className={`flex flex-col relative ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      } ${isFloating ? 'border-2' : 'border-r'}`}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-2 py-1.5 border-b ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        } ${isFloating ? 'cursor-move' : ''}`}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-1.5">
          {isFloating && <Move size={12} className="text-gray-400" />}
          <Youtube size={14} className="text-red-600" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            YouTube
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {!isMobile && (
            <>
              <button
                onClick={() => setIsFloating(!isFloating)}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
                title={isFloating ? 'Dock to sidebar' : 'Float window'}
              >
                {isFloating ? <Pin size={12} /> : <PinOff size={12} />}
              </button>
              <a
                href={(() => {
                  let time = 0;
                  try {
                    if (playerRef.current) {
                      time = Math.floor(playerRef.current.getCurrentTime());
                    }
                  } catch (e) {
                    // Player might not be ready.
                  }
                  const url = new URL(youtubeUrl);
                  if (time > 0) {
                    url.searchParams.set('t', `${time}s`);
                  }
                  return url.toString();
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
                title="Open in YouTube at current time"
              >
                <ExternalLink size={12} />
              </a>
              {onToggleMinimize && (
                <button
                  onClick={onToggleMinimize}
                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                  title={isMinimized ? 'Expand' : 'Minimize'}
                >
                  {isMinimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                </button>
              )}
            </>
          )}
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
            title="Close YouTube player"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Player container */}
      {!isMinimized && (
        <div
          className="relative bg-black"
          style={{
            paddingBottom: '56.25%', // 16:9 aspect ratio
            height: 0,
            overflow: 'hidden'
          }}
        >
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-red-500">
                <Youtube size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">{error}</p>
              </div>
            </div>
          ) : !isAPIReady ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-red-600 rounded-full mx-auto mb-2" />
                <p className="text-xs">Loading...</p>
              </div>
            </div>
          ) : (
            <div
              id={playerId}
              className="absolute inset-0"
              style={{ width: '100%', height: '100%' }}
            />
          )}
        </div>
      )}

      {/* Minimized state - show thumbnail or status */}
      {isMinimized && (
        <div
          className={`px-2 py-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
          onClick={onToggleMinimize}
          style={{ cursor: 'pointer' }}
        >
          Click to expand
        </div>
      )}

      {/* Resize handle for floating mode */}
      {isFloating && !isMinimized && (
        <div
          onMouseDown={handleResizeMouseDown}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.5) 50%)',
          }}
          title="Drag to resize"
        />
      )}
    </div>
  );
};

export default YouTubePlayer;
